'use client';

import { signInWithMagicLink } from '@/api/auth';
import Turnstile, { useTurnstile } from '@/components/auth/Turnstile';
import { MAGIC_LINK_SCHEMA } from '@/schema/auth';
import { Anchor, Box, Button, Center, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import Link from 'next/link';
import React, { useState } from 'react';
import { FiMail } from 'react-icons/fi';

const MagicLink = () => {
  const [emailSent, setEmailSent] = useState(false);
  const captcha = useTurnstile();

  const magicLinkMutation = useMutation({
    mutationFn: (values: { email: string }) => signInWithMagicLink({ ...values, captchaToken: captcha.token }),
    onError: (error: any) => {
      captcha.reset(); // Turnstile tokens are single-use
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
    onSuccess: () => {
      setEmailSent(true);
      notifications.show({
        title: 'Success',
        message: 'Check your email for the magic link!',
        color: 'green',
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: MAGIC_LINK_SCHEMA,
    onSubmit: async values => {
      magicLinkMutation.mutateAsync(values);
    },
  });

  if (emailSent) {
    return (
      <Stack justify="center" w="100%" h="100vh" align="center" px="sm">
        <Box
          w="100%"
          px={{
            sm: 'lg',
            md: 100,
            lg: 150,
          }}
        >
          <Center mb="xl">
            <FiMail size={48} />
          </Center>
          <Title order={3} mb="md" ta="center">
            Check your email
          </Title>
          <Text ta="center" c="dimmed" mb="xl">
            We sent a magic link to <strong>{formik.values.email}</strong>. Click the link in your email to sign in.
          </Text>
          <Button
            fullWidth
            variant="light"
            radius="md"
            size="md"
            onClick={() => {
              setEmailSent(false);
              formik.resetForm();
            }}
          >
            Use a different email
          </Button>
          <Center mt="xl">
            <Anchor component={Link} href="/" c="pivit">
              Back to login
            </Anchor>
          </Center>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack justify="center" w="100%" h="100vh" align="center" px="sm">
      <Box
        w="100%"
        px={{
          sm: 'lg',
          md: 100,
          lg: 150,
        }}
      >
        <Title order={3} mb="md">
          Sign in with Magic Link
        </Title>
        <Text c="dimmed" mb="xl">
          Enter your email address and we&apos;ll send you a link to sign in instantly.
        </Text>
        {/* @ts-ignore */}
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Stack gap="md">
            <TextInput
              label="Email address"
              value={formik?.values.email}
              onChange={formik?.handleChange}
              name="email"
              error={formik.touched.email && formik.errors.email}
              size="md"
              variant="filled"
              placeholder="you@example.com"
            />

            <Turnstile ref={captcha.ref} action="magic-link" onToken={captcha.setToken} onError={captcha.onError} />

            <Button
              loading={magicLinkMutation.isLoading}
              loaderProps={{
                type: 'bars',
              }}
              fullWidth
              radius="md"
              size="md"
              type="submit"
              disabled={magicLinkMutation.isLoading || !captcha.ready}
            >
              Send Magic Link
            </Button>
          </Stack>
        </form>
        <Center mt="xl">
          <Group gap={5}>
            <Text>Prefer a password? </Text>
            <Anchor component={Link} c="pivit" href="/">
              Login with password
            </Anchor>
          </Group>
        </Center>
      </Box>
    </Stack>
  );
};

export default MagicLink;
